import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { RouteStep } from "../components/quote/RouteStep";
import { ItemPicker } from "../components/quote/ItemPicker";
import { MethodStep } from "../components/quote/MethodStep";
import { PhotoUpload } from "../components/quote/PhotoUpload";
import { QuoteDetailsForm } from "../components/quote/QuoteDetailsForm";
import { QuoteSummary } from "../components/quote/QuoteSummary";
import { QuoteSuccess } from "../components/quote/QuoteSuccess";

const EMPTY = {
  request_type: "quote", full_name: "", email: "", phone: "", address: "", city: "", province_state: "",
  postal_code: "", receiver_name: "", receiver_phone: "", preferred_date: "", notes: "", agree_prohibited: false,
};

function useQuoteCalc(originId, destId, method, items) {
  const [calc, setCalc] = useState(null);
  useEffect(() => {
    if (!originId || !destId) { setCalc(null); return undefined; }
    const t = setTimeout(() => {
      api.post("/quotes/calculate", { origin_id: originId, destination_id: destId, method, items })
        .then((r) => setCalc(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [originId, destId, method, items]);
  return calc;
}

export default function Quote() {
  const [pricing, setPricing] = useState(null);
  const [originId, setOriginId] = useState("");
  const [destId, setDestId] = useState("");
  const [items, setItems] = useState([]);
  const [method, setMethod] = useState("dropoff");
  const [photos, setPhotos] = useState([]);
  const [details, setDetails] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const calc = useQuoteCalc(originId, destId, method, items);

  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const origin = pricing?.origins.find((o) => o.id === originId);
  const rate = pricing?.rates?.[originId]?.[destId];

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) { toast.error("Add at least one item"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...details, origin_id: originId, destination_id: destId, method, items }));
      photos.forEach((p) => fd.append("files", p.file));
      const { data } = await api.post("/quotes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setResult(null); setItems([]); setPhotos([]); setDetails(EMPTY); };

  if (result) return <QuoteSuccess result={result} onReset={reset} />;

  return (
    <div data-testid="quote-page">
      <section className="bg-[#1E2A78] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 text-[#FFD700] mb-3">
            <Calculator className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Instant Quote</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight max-w-3xl">
            Get your shipping <span className="text-[#FFD700]">price now.</span>
          </h1>
          <p className="mt-6 text-slate-200 max-w-2xl">
            Barrels, boxes, furniture, pallets — from Canada or the USA to Jamaica and the Caribbean. Your price updates as you add items.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#F1F5F9]">
        {!pricing ? (
          <div className="text-center text-slate-500" data-testid="quote-loading">Loading prices...</div>
        ) : (
          <form onSubmit={submit} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="quote-form">
            <div className="lg:col-span-2 space-y-6 min-w-0">
              <RouteStep pricing={pricing} originId={originId} setOriginId={setOriginId} destId={destId} setDestId={setDestId} origin={origin} />
              <ItemPicker items={items} setItems={setItems} rate={rate} currency={origin?.currency} lines={calc?.lines} />
              <MethodStep method={method} setMethod={setMethod} origin={origin} liftgateFee={pricing.liftgate_fee} hasPallet={items.some((i) => i.type === "pallet")} />
              <PhotoUpload photos={photos} setPhotos={setPhotos} />
              <QuoteDetailsForm details={details} setDetails={setDetails} origin={origin} />
            </div>
            <QuoteSummary calc={calc} submitting={submitting} requestType={details.request_type} itemCount={items.length} />
          </form>
        )}
      </section>
    </div>
  );
}
